import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API from 'shared/api';
import {
  FiDollarSign, FiArrowUpCircle, FiArrowDownCircle, FiRefreshCw,
  FiCreditCard, FiTrendingUp, FiCalendar,
} from 'react-icons/fi';
import { LineChart, Donut } from 'shared/components/Dashboard/charts';

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function Tesoreria() {
  const { tienePermiso } = useAuth();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const v = (c) => tienePermiso(c);

  const [resumen, setResumen] = useState(null);
  const [flujo, setFlujo] = useState([]);
  const [dias, setDias] = useState('30');
  const [diasAtras, setDiasAtras] = useState('7');
  const [cargando, setCargando] = useState(false);

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const [r1, r2] = await Promise.all([
        API.get('/tesoreria/resumen'),
        API.get('/tesoreria/flujo', { params: { dias: Number(dias || 30), dias_atras: Number(diasAtras || 7) } }),
      ]);
      setResumen(r1.data?.data || null);
      setFlujo(r2.data?.data || []);
    } catch (e) {
      console.error('Error cargando tesorería', e);
    } finally {
      setCargando(false);
    }
  }, [dias, diasAtras]);

  useEffect(() => { load(); }, [load]);

  const porPagar = resumen?.por_pagar ?? 0;
  const porCobrar = resumen?.por_cobrar ?? 0;
  const neto = resumen?.neto ?? 0;
  const entradasHoy = resumen?.entradas_hoy ?? 0;
  const salidasHoy = resumen?.salidas_hoy ?? 0;

  const labels = flujo.map((d) => String(d.fecha).slice(5));
  const entradasSerie = flujo.map((d) => d.entradas);
  const salidasSerie = flujo.map((d) => d.salidas);

  const donutPagar = [
    { label: 'Corriente', value: resumen?.aging_pagar?.corriente || 0, color: COL.green },
    { label: 'Vencidas 1-30', value: resumen?.aging_pagar?.vencidas_30 || 0, color: COL.amber },
    { label: 'Vencidas 31-60', value: resumen?.aging_pagar?.vencidas_60 || 0, color: COL.orange },
    { label: 'Vencidas 61+', value: resumen?.aging_pagar?.vencidas_90 || 0, color: COL.red },
  ];
  const donutCobrar = [
    { label: 'Corriente', value: resumen?.aging_cobrar?.corriente || 0, color: COL.green },
    { label: 'Vencidas 1-30', value: resumen?.aging_cobrar?.vencidas_30 || 0, color: COL.amber },
    { label: 'Vencidas 31-60', value: resumen?.aging_cobrar?.vencidas_60 || 0, color: COL.orange },
    { label: 'Vencidas 61+', value: resumen?.aging_cobrar?.vencidas_90 || 0, color: COL.red },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 22, fontWeight: 700, color: COL.text }}>Tesorería</h2>
          <div style={{ fontSize: 13, color: COL.muted, marginTop: 3 }}>
            Flujo de caja: dinero que entra y sale
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select value={dias} onChange={(e) => setDias(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13 }}>
            <option value="7">7 días</option>
            <option value="15">15 días</option>
            <option value="30">30 días</option>
            <option value="60">60 días</option>
            <option value="90">90 días</option>
          </select>
          <select value={diasAtras} onChange={(e) => setDiasAtras(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13 }}>
            <option value="7">Historial 7d</option>
            <option value="15">Historial 15d</option>
            <option value="30">Historial 30d</option>
          </select>
          <Boton COL={COL} ghost onClick={load} disabled={cargando}><FiRefreshCw size={14} /> Actualizar</Boton>
        </div>
      </div>

      {cargando && !resumen ? (
        <div style={{ textAlign: 'center', padding: 40, color: COL.muted }}>Cargando…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
            <KpiCard COL={COL} isMobile={isMobile} icon={FiArrowUpCircle} label="Entró hoy" value={money(entradasHoy)} color={COL.green} />
            <KpiCard COL={COL} isMobile={isMobile} icon={FiArrowDownCircle} label="Salió hoy" value={money(salidasHoy)} color={COL.red} />
            <KpiCard COL={COL} isMobile={isMobile} icon={FiTrendingUp} label="Por cobrar" value={money(porCobrar)} color={COL.green} permiso="vis.tesoreria.por_cobrar" v={v} />
            <KpiCard COL={COL} isMobile={isMobile} icon={FiCreditCard} label="Por pagar" value={money(porPagar)} color={COL.red} permiso="vis.tesoreria.por_pagar" v={v} />
            <KpiCard COL={COL} isMobile={isMobile} icon={FiDollarSign} label="Neto (cobrar - pagar)" value={money(neto)} color={neto >= 0 ? COL.green : COL.red} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 16 }}>
            {v('vis.tesoreria.por_cobrar') && (
              <Card COL={COL} title="Aging por cobrar" icon={FiCreditCard}>
                <Donut data={donutCobrar} />
              </Card>
            )}
            {v('vis.tesoreria.por_pagar') && (
              <Card COL={COL} title="Aging por pagar" icon={FiDollarSign}>
                <Donut data={donutPagar} />
              </Card>
            )}
          </div>

          <Card COL={COL} title="Flujo de caja" icon={FiCalendar} hint="Histórico real + proyección de créditos por vencer">
            {flujo.length === 0 ? (
              <div style={{ textAlign: 'center', color: COL.muted, padding: 20, fontSize: 13 }}>Sin movimientos en el rango seleccionado</div>
            ) : (
              <LineChart
                labels={labels}
                series={[
                  { name: 'Entradas', values: entradasSerie, color: COL.green },
                  { name: 'Salidas', values: salidasSerie, color: COL.red },
                ]}
                fmt={(x) => money(x)}
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({ COL, isMobile, icon: Icon, label, value, color, permiso, v }) {
  if (permiso && v && !v(permiso)) return null;
  return (
    <div style={{ padding: isMobile ? 14 : 16, borderRadius: 12, background: COL.surface, border: `1px solid ${COL.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: COL.blueBg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: COL.muted }}>{label}</div>
        <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color, wordBreak: 'break-word' }}>{value}</div>
      </div>
    </div>
  );
}

function Card({ COL, title, icon: Icon, hint, children }) {
  return (
    <div style={{ padding: 16, borderRadius: 12, background: COL.surface, border: `1px solid ${COL.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {Icon && <Icon size={16} color={COL.pri2} />}
        <span style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>{title}</span>
        {hint && <span style={{ fontSize: 11, color: COL.muted }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Boton({ COL, ghost, onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, border: ghost ? `1px solid ${COL.border}` : 'none', cursor: disabled ? 'not-allowed' : 'pointer', background: ghost ? 'transparent' : COL.pri2, color: ghost ? COL.text : '#fff', fontSize: 13, fontWeight: 600, minHeight: 38, opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}
